import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { T } from 'react-translator-component';
import styled from 'styled-components';

import { combiningObjects } from './listFormater';
import TransactionsCardInnerContent from './TransactionsCardInnerContent/TransactionsCardInnerContent';

import { ITransaction } from '../../../types/responses/Transactions';
import { hasFinancialIndicators } from '../../../utils/hasFinancialIndicators/hasFinancialIndicators';
import { isMonetizationProUser } from '../../../utils/isMonetizationProUser/isMonetizationProUser';
import { isTerpelTenant } from '../../../utils/isTerpelTenant/isTerpelTenant';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import { ResendNFEModalProps } from '../../pages/TransactionList/TransactionList';
import CardListConstructor from '../CardListConstructor/CardListConstructor2';
import EmptySearch from '../EmptySearch';

const removeDuplicateTransactions = (
  transactions: ITransaction[],
): ITransaction[] => {
  const uniqueTransactions = new Map<number, ITransaction>();
  const safeTransactions = Array.isArray(transactions) ? transactions : [];

  safeTransactions.forEach((transaction) => {
    if (!uniqueTransactions.has(transaction.id)) {
      uniqueTransactions.set(transaction.id, transaction);
    }
  });

  return Array.from(uniqueTransactions.values());
};

interface Props {
  transactions: ITransaction[];
  paginationData: any;
  setpaginationData: any;
  childRef: any;
  loading: any;
  selectMode: any;
  checkedList: any;
  setCheckedList: any;
  count: any;
  currentPage: any;
  setCurrentPage: any;
  pageSize: any;
  headerFilter: any;
  setHeaderFilter: any;
  setShowResendNFEModal: React.Dispatch<
    React.SetStateAction<ResendNFEModalProps>
  >;
}

export const TransactionsTable: FC<Props> = ({
  transactions,
  paginationData,
  setpaginationData,
  childRef,
  loading,
  selectMode,
  checkedList,
  setCheckedList,
  count,
  currentPage,
  setCurrentPage,
  pageSize,
  headerFilter,
  setHeaderFilter,
  setShowResendNFEModal,
}) => {
  const user: any = JSON.parse(localStorage.getItem('auth-user') as any);

  const shouldShowNFColumn = user?.permissions.some(
    (permission) =>
      permission.route === '/transactions/show_nf_column' &&
      !!permission.status,
  );

  const [data, setdata] = useState<any>({});

  const paginate = useCallback(() => childRef?.current?.paginate(), [childRef]);

  const HEADER = useMemo(() => {
    return [
      { accessor: 'TRANSACTION_PK', text: T('Recharge (ID)'), size: '7%' },
      { accessor: 'CHARGE_BOX_ID', text: T('Station'), size: '12%' },
      { accessor: 'CONNECTOR_ID', text: T('Connector (ID)'), size: '6%' },
      { accessor: 'CHARGEBOX_UF', text: T('State'), size: '5%' },
      { accessor: 'CHARGEBOX_CITY', text: T('City'), size: '9%' },
      { accessor: 'ID_TAG', text: T('User (Tag)'), size: '12%' },
      { accessor: 'START_TIMESTAMP', text: T('Start - End'), size: '15%' },
      { accessor: 'DURATION', text: T('Duration'), size: '7%' },
      ...(isMonetizationProUser() || isTerpelTenant()
        ? [{ accessor: 'NF', text: T('NF'), size: '5%' }]
        : []),
      ...((isMonetizationProUser() || isTerpelTenant()) &&
      hasFinancialIndicators()
        ? [{ accessor: 'INCOME', text: T('Income ($)'), size: '11%' }]
        : []),
      { accessor: 'ENERGY', text: `${T('Energy')} (kWh)`, size: '6%' },
    ];
  }, []);

  const filteredHeaderByNFPermission = useMemo(() => {
    if (shouldShowNFColumn) {
      return HEADER;
    }
    return HEADER.filter((h) => h.accessor !== 'NF');
  }, [HEADER, shouldShowNFColumn]);

  const handleTransactions = useCallback(() => {
    const uniqueTransactions = removeDuplicateTransactions(transactions);

    paginate();
    setdata(
      combiningObjects(
        uniqueTransactions,
        shouldShowNFColumn,
        filteredHeaderByNFPermission,
        setShowResendNFEModal,
      ),
    );
    setpaginationData(uniqueTransactions);
  }, [
    paginate,
    filteredHeaderByNFPermission,
    setShowResendNFEModal,
    setpaginationData,
    shouldShowNFColumn,
    transactions,
  ]);

  useEffect(() => {
    handleTransactions();
  }, [handleTransactions]);

  useEffect(() => {
    paginate();
  }, [paginationData, paginate]);

  return (
    <Div>
      {loading ? (
        <ListSkeleton
          style={{ paddingTop: 20, paddingLeft: 20, paddingRight: 20 }}
        />
      ) : !loading && !data.cardsList?.length ? (
        <EmptySearch />
      ) : (
        <CardListConstructor
          childRef={childRef}
          data={data}
          InnerComponent={TransactionsCardInnerContent}
          paginationCallback={(newList) =>
            setdata(
              combiningObjects(
                newList,
                shouldShowNFColumn,
                filteredHeaderByNFPermission,
                setShowResendNFEModal,
              ),
            )
          }
          pureData={paginationData}
          checkboxAvailable={true}
          selectMode={selectMode}
          checkedList={checkedList}
          setCheckedList={setCheckedList}
          count={count}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          headerFilter={headerFilter}
          setHeaderFilter={setHeaderFilter}
          noOrderHeader
        />
      )}
    </Div>
  );
};

const Div = styled.div`
  padding-bottom: 60px;

  .card_header .card_value_label .main_card_text {
    font-size: 12px;
  }
  .thunder_icon {
    width: 20px;
    margin-right: 5px;
  }
  .coin_icon {
    width: 20px;
    margin-right: 5px;
  }
`;
